import { useCallback, useEffect, useMemo, useState } from "react";

/* ── Types ─────────────────────────────────────────────────── */

type StepMode = "chat" | "canvas" | "file_edit" | "shell" | "review";

interface RecipeStep {
  order: number;
  instruction: string;
  mode: StepMode;
  expected_output?: string;
}

interface RecipeSummary {
  id: string;
  name: string;
  description: string;
  version: string;
  author: string;
  tags: string[];
  step_count: number;
  required_tools: string[];
  created_at: string;
}

interface RecipeDetail {
  id: string;
  name: string;
  description: string;
  version: string;
  author: string;
  tags: string[];
  steps: RecipeStep[];
  system_prompt?: string;
  required_tools: string[];
  created_at: string;
}

type View = "list" | "detail" | "create" | "import";

const STEP_MODES: StepMode[] = ["chat", "canvas", "file_edit", "shell", "review"];

const MODE_LABELS: Record<StepMode, string> = {
  chat: "Chat",
  canvas: "Canvas",
  file_edit: "File Edit",
  shell: "Shell",
  review: "Review",
};

const MODE_ICONS: Record<StepMode, string> = {
  chat: "M21 15a2 2 0 01-2 2H7l-4 4V5a2 2 0 012-2h14a2 2 0 012 2z",
  canvas: "M3 3h18v18H3z M3 9h18 M9 21V9",
  file_edit: "M11 4H4a2 2 0 00-2 2v14a2 2 0 002 2h14a2 2 0 002-2v-7 M18.5 2.5a2.12 2.12 0 013 3L12 15l-4 1 1-4 9.5-9.5z",
  shell: "M4 17l6-6-6-6 M12 19h8",
  review: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z M12 9a3 3 0 100 6 3 3 0 000-6z",
};

/* ── Component ─────────────────────────────────────────────── */

export function RecipesWorkbench() {
  const [view, setView] = useState<View>("list");
  const [recipes, setRecipes] = useState<RecipeSummary[]>([]);
  const [selected, setSelected] = useState<RecipeDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState("");
  const [importJson, setImportJson] = useState("");
  const [importError, setImportError] = useState("");
  const [toast, setToast] = useState("");

  // ── Create form state
  const [formName, setFormName] = useState("");
  const [formDesc, setFormDesc] = useState("");
  const [formVersion, setFormVersion] = useState("1.0.0");
  const [formAuthor, setFormAuthor] = useState("");
  const [formTags, setFormTags] = useState("");
  const [formPrompt, setFormPrompt] = useState("");
  const [formTools, setFormTools] = useState("");
  const [formSteps, setFormSteps] = useState<RecipeStep[]>([
    { order: 1, instruction: "", mode: "chat", expected_output: "" },
  ]);

  const flash = useCallback((msg: string) => {
    setToast(msg);
    setTimeout(() => setToast(""), 2400);
  }, []);

  /* ── Data fetching ──────────────────────────────────────── */

  const fetchRecipes = useCallback(async () => {
    try {
      const res = await fetch("/api/recipes");
      const data = await res.json();
      setRecipes(data.recipes ?? []);
    } catch {
      /* offline fallback */
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchDetail = useCallback(async (id: string) => {
    try {
      const res = await fetch(`/api/recipes/${id}`);
      if (!res.ok) return;
      const data: RecipeDetail = await res.json();
      setSelected(data);
      setView("detail");
    } catch {
      /* ignore */
    }
  }, []);

  useEffect(() => {
    fetchRecipes();
  }, [fetchRecipes]);

  /* ── Actions ────────────────────────────────────────────── */

  const handleDelete = useCallback(
    async (id: string) => {
      await fetch(`/api/recipes/${id}`, { method: "DELETE" });
      setSelected(null);
      setView("list");
      flash(`Deleted recipe: ${id}`);
      fetchRecipes();
    },
    [fetchRecipes, flash],
  );

  const handleExport = useCallback(async (id: string) => {
    const res = await fetch(`/api/recipes/${id}/export`, { method: "POST" });
    const data = await res.json();
    if (data.json) {
      await navigator.clipboard.writeText(data.json);
      flash("Recipe JSON copied to clipboard");
    }
  }, [flash]);

  const handleImport = useCallback(async () => {
    setImportError("");
    try {
      const parsed = JSON.parse(importJson);
      const res = await fetch("/api/recipes", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(parsed),
      });
      const data = await res.json();
      if (data.ok) {
        setImportJson("");
        setView("list");
        flash(`Imported: ${data.name}`);
        fetchRecipes();
      } else {
        setImportError(data.error ?? "Import failed");
      }
    } catch (e) {
      setImportError(`Invalid JSON: ${e}`);
    }
  }, [importJson, fetchRecipes, flash]);

  const handleCreate = useCallback(async () => {
    const id = formName
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, "-")
      .replace(/^-|-$/g, "");
    if (!id) return;

    const recipe = {
      id,
      name: formName,
      description: formDesc,
      version: formVersion,
      author: formAuthor || "user",
      tags: formTags
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      steps: formSteps.filter((s) => s.instruction.trim()),
      system_prompt: formPrompt || undefined,
      required_tools: formTools
        .split(",")
        .map((t) => t.trim())
        .filter(Boolean),
      created_at: new Date().toISOString(),
    };

    const res = await fetch("/api/recipes", {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify(recipe),
    });
    const data = await res.json();
    if (data.ok) {
      resetForm();
      setView("list");
      flash(`Created: ${data.name}`);
      fetchRecipes();
    }
  }, [
    formName,
    formDesc,
    formVersion,
    formAuthor,
    formTags,
    formPrompt,
    formTools,
    formSteps,
    fetchRecipes,
    flash,
  ]);

  const resetForm = () => {
    setFormName("");
    setFormDesc("");
    setFormVersion("1.0.0");
    setFormAuthor("");
    setFormTags("");
    setFormPrompt("");
    setFormTools("");
    setFormSteps([{ order: 1, instruction: "", mode: "chat", expected_output: "" }]);
  };

  const addStep = () => {
    setFormSteps((prev) => [
      ...prev,
      { order: prev.length + 1, instruction: "", mode: "chat", expected_output: "" },
    ]);
  };

  const updateStep = (idx: number, patch: Partial<RecipeStep>) => {
    setFormSteps((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)));
  };

  const removeStep = (idx: number) => {
    setFormSteps((prev) =>
      prev.filter((_, i) => i !== idx).map((s, i) => ({ ...s, order: i + 1 })),
    );
  };

  /* ── Filtered list ──────────────────────────────────────── */

  const filtered = useMemo(() => {
    const q = query.toLowerCase();
    if (!q) return recipes;
    return recipes.filter(
      (r) =>
        r.name.toLowerCase().includes(q) ||
        r.description.toLowerCase().includes(q) ||
        r.tags.some((t) => t.toLowerCase().includes(q)),
    );
  }, [recipes, query]);

  /* ── Render ─────────────────────────────────────────────── */

  return (
    <main className="ui-shell" style={{ padding: 0, minHeight: "100vh" }}>
      <section style={S.root}>
        {/* Header */}
        <header style={S.header}>
          <div>
            <p style={S.kicker}>Agent Recipes</p>
            <h1 style={S.title}>Workflow Templates</h1>
            <p style={S.subtitle}>
              Shareable, forkable agent workflow templates — npm for agent workflows.
            </p>
          </div>
          <div style={S.headerActions}>
            <button style={S.btnPrimary} onClick={() => { resetForm(); setView("create"); }}>
              + New Recipe
            </button>
            <button style={S.btnSecondary} onClick={() => { setImportJson(""); setImportError(""); setView("import"); }}>
              Import JSON
            </button>
          </div>
        </header>

        {/* Toast */}
        {toast && <div style={S.toast}>{toast}</div>}

        {/* Body */}
        <div style={S.body}>
          {/* ── List view ─────────────────────────────────── */}
          {(view === "list" || view === "detail") && (
            <aside style={S.sidebar}>
              <input
                style={S.search}
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Search recipes..."
                autoComplete="off"
              />
              <div style={S.listScroll}>
                {loading && <p style={S.muted}>Loading...</p>}
                {!loading && filtered.length === 0 && (
                  <p style={S.muted}>No recipes found.</p>
                )}
                {filtered.map((r) => (
                  <button
                    key={r.id}
                    style={{
                      ...S.listItem,
                      ...(selected?.id === r.id ? S.listItemActive : {}),
                    }}
                    onClick={() => fetchDetail(r.id)}
                  >
                    <div style={S.listItemHead}>
                      <strong style={S.listItemName}>{r.name}</strong>
                      <span style={S.listItemMeta}>{r.step_count} steps</span>
                    </div>
                    <p style={S.listItemDesc}>{r.description}</p>
                    <div style={S.tagRow}>
                      {r.tags.slice(0, 4).map((t) => (
                        <span key={t} style={S.tag}>
                          {t}
                        </span>
                      ))}
                    </div>
                  </button>
                ))}
              </div>
            </aside>
          )}

          {/* ── Detail view ───────────────────────────────── */}
          {view === "detail" && selected && (
            <article style={S.detail}>
              <div style={S.detailHeader}>
                <div>
                  <h2 style={S.detailTitle}>{selected.name}</h2>
                  <p style={S.detailDesc}>{selected.description}</p>
                  <div style={S.detailMeta}>
                    <span>v{selected.version}</span>
                    <span style={S.metaDot} />
                    <span>by {selected.author}</span>
                    <span style={S.metaDot} />
                    <span>{selected.steps.length} steps</span>
                  </div>
                </div>
                <div style={S.detailActions}>
                  <button style={S.btnPrimary} onClick={() => handleExport(selected.id)}>
                    Export JSON
                  </button>
                  <button
                    style={S.btnDanger}
                    onClick={() => handleDelete(selected.id)}
                  >
                    Delete
                  </button>
                </div>
              </div>

              {/* System prompt */}
              {selected.system_prompt && (
                <div style={S.block}>
                  <h3 style={S.blockTitle}>System Prompt</h3>
                  <pre style={S.pre}>{selected.system_prompt}</pre>
                </div>
              )}

              {/* Required tools */}
              {selected.required_tools.length > 0 && (
                <div style={S.block}>
                  <h3 style={S.blockTitle}>Required Tools</h3>
                  <div style={S.tagRow}>
                    {selected.required_tools.map((t) => (
                      <span key={t} style={S.toolTag}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}

              {/* Steps */}
              <div style={S.block}>
                <h3 style={S.blockTitle}>Steps</h3>
                <div style={S.stepList}>
                  {selected.steps.map((step) => (
                    <div key={step.order} style={S.stepCard}>
                      <div style={S.stepHeader}>
                        <span style={S.stepOrder}>{step.order}</span>
                        <span style={S.stepMode}>
                          <svg
                            width="14"
                            height="14"
                            viewBox="0 0 24 24"
                            fill="none"
                            stroke="currentColor"
                            strokeWidth="2"
                            strokeLinecap="round"
                            strokeLinejoin="round"
                          >
                            <path d={MODE_ICONS[step.mode] ?? MODE_ICONS.chat} />
                          </svg>
                          {MODE_LABELS[step.mode] ?? step.mode}
                        </span>
                      </div>
                      <p style={S.stepInstruction}>{step.instruction}</p>
                      {step.expected_output && (
                        <p style={S.stepExpected}>
                          Expected: {step.expected_output}
                        </p>
                      )}
                    </div>
                  ))}
                </div>
              </div>

              {/* Tags */}
              {selected.tags.length > 0 && (
                <div style={S.block}>
                  <h3 style={S.blockTitle}>Tags</h3>
                  <div style={S.tagRow}>
                    {selected.tags.map((t) => (
                      <span key={t} style={S.tag}>
                        {t}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </article>
          )}

          {/* ── Create form ───────────────────────────────── */}
          {view === "create" && (
            <article style={S.formPane}>
              <div style={S.formHeader}>
                <h2 style={S.detailTitle}>Create Recipe</h2>
                <button style={S.btnSecondary} onClick={() => setView("list")}>
                  Cancel
                </button>
              </div>

              <div style={S.formGrid}>
                <label style={S.label}>
                  Name
                  <input
                    style={S.input}
                    value={formName}
                    onChange={(e) => setFormName(e.target.value)}
                    placeholder="My Workflow"
                  />
                </label>
                <label style={S.label}>
                  Version
                  <input
                    style={S.input}
                    value={formVersion}
                    onChange={(e) => setFormVersion(e.target.value)}
                    placeholder="1.0.0"
                  />
                </label>
                <label style={S.label}>
                  Author
                  <input
                    style={S.input}
                    value={formAuthor}
                    onChange={(e) => setFormAuthor(e.target.value)}
                    placeholder="your-name"
                  />
                </label>
                <label style={S.label}>
                  Tags (comma-separated)
                  <input
                    style={S.input}
                    value={formTags}
                    onChange={(e) => setFormTags(e.target.value)}
                    placeholder="review, quality, testing"
                  />
                </label>
              </div>

              <label style={S.label}>
                Description
                <textarea
                  style={S.textarea}
                  value={formDesc}
                  onChange={(e) => setFormDesc(e.target.value)}
                  placeholder="What does this recipe do?"
                  rows={2}
                />
              </label>

              <label style={S.label}>
                System Prompt (optional)
                <textarea
                  style={S.textarea}
                  value={formPrompt}
                  onChange={(e) => setFormPrompt(e.target.value)}
                  placeholder="Custom system prompt for the agent..."
                  rows={2}
                />
              </label>

              <label style={S.label}>
                Required Tools (comma-separated)
                <input
                  style={S.input}
                  value={formTools}
                  onChange={(e) => setFormTools(e.target.value)}
                  placeholder="file-read, file-write, shell"
                />
              </label>

              {/* Steps editor */}
              <div style={{ marginTop: 16 }}>
                <div style={S.stepsEditorHead}>
                  <h3 style={S.blockTitle}>Steps</h3>
                  <button style={S.btnSmall} onClick={addStep}>
                    + Add Step
                  </button>
                </div>
                {formSteps.map((step, idx) => (
                  <div key={idx} style={S.stepEditor}>
                    <div style={S.stepEditorTop}>
                      <span style={S.stepOrder}>{idx + 1}</span>
                      <select
                        style={S.select}
                        value={step.mode}
                        onChange={(e) =>
                          updateStep(idx, { mode: e.target.value as StepMode })
                        }
                      >
                        {STEP_MODES.map((m) => (
                          <option key={m} value={m}>
                            {MODE_LABELS[m]}
                          </option>
                        ))}
                      </select>
                      {formSteps.length > 1 && (
                        <button
                          style={S.btnRemove}
                          onClick={() => removeStep(idx)}
                          title="Remove step"
                        >
                          x
                        </button>
                      )}
                    </div>
                    <input
                      style={S.input}
                      value={step.instruction}
                      onChange={(e) =>
                        updateStep(idx, { instruction: e.target.value })
                      }
                      placeholder="Instruction for this step..."
                    />
                    <input
                      style={{ ...S.input, fontSize: 12, color: "#7a8a9a" }}
                      value={step.expected_output ?? ""}
                      onChange={(e) =>
                        updateStep(idx, { expected_output: e.target.value || undefined })
                      }
                      placeholder="Expected output (optional)"
                    />
                  </div>
                ))}
              </div>

              <button
                style={{ ...S.btnPrimary, marginTop: 20, width: "100%" }}
                onClick={handleCreate}
                disabled={!formName.trim() || formSteps.every((s) => !s.instruction.trim())}
              >
                Create Recipe
              </button>
            </article>
          )}

          {/* ── Import view ───────────────────────────────── */}
          {view === "import" && (
            <article style={S.formPane}>
              <div style={S.formHeader}>
                <h2 style={S.detailTitle}>Import Recipe</h2>
                <button style={S.btnSecondary} onClick={() => setView("list")}>
                  Cancel
                </button>
              </div>
              <p style={S.muted}>
                Paste the JSON for a recipe below. You can get this JSON from the
                Export button on any recipe.
              </p>
              <textarea
                style={{ ...S.textarea, minHeight: 220, fontFamily: "monospace" }}
                value={importJson}
                onChange={(e) => setImportJson(e.target.value)}
                placeholder='{ "id": "my-recipe", "name": "My Recipe", ... }'
              />
              {importError && <p style={S.error}>{importError}</p>}
              <button
                style={{ ...S.btnPrimary, marginTop: 12 }}
                onClick={handleImport}
                disabled={!importJson.trim()}
              >
                Import
              </button>
            </article>
          )}

          {/* Empty detail pane when list is shown but nothing selected */}
          {view === "list" && !selected && (
            <article style={S.emptyDetail}>
              <div style={S.emptyIcon}>
                <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="#3a4a5a" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                  <path d="M4 19.5A2.5 2.5 0 016.5 17H20" />
                  <path d="M6.5 2H20v20H6.5A2.5 2.5 0 014 19.5v-15A2.5 2.5 0 016.5 2z" />
                  <path d="M8 7h8 M8 11h6" />
                </svg>
              </div>
              <p style={S.muted}>
                Select a recipe to view its details, or create a new one.
              </p>
            </article>
          )}
        </div>
      </section>
    </main>
  );
}

/* ── Inline styles (dark terminal theme) ──────────────────── */

const C = {
  bg: "#0d0d0d",
  surface: "#1a1b26",
  surface2: "#1e1e2e",
  border: "rgba(255,255,255,0.06)",
  accent: "#50fa7b",
  accentDim: "rgba(80,250,123,0.12)",
  text: "#f8f8f2",
  textMuted: "#6272a4",
  danger: "#ff5555",
  dangerDim: "rgba(241,91,91,0.12)",
  cyan: "#8be9fd",
  violet: "#bd93f9",
} as const;

const S: Record<string, React.CSSProperties> = {
  root: {
    display: "flex",
    flexDirection: "column",
    height: "100%",
    minHeight: "100vh",
    background: C.bg,
    color: C.text,
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  },
  header: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-end",
    padding: "28px 32px 20px",
    borderBottom: `1px solid ${C.border}`,
  },
  kicker: {
    fontSize: 11,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.1em",
    color: C.accent,
    margin: 0,
  },
  title: {
    fontSize: 22,
    fontWeight: 700,
    margin: "4px 0 0",
    color: C.text,
  },
  subtitle: {
    fontSize: 13,
    color: C.textMuted,
    margin: "4px 0 0",
  },
  headerActions: {
    display: "flex",
    gap: 8,
  },
  toast: {
    position: "fixed" as const,
    top: 16,
    right: 16,
    background: C.accentDim,
    color: C.accent,
    padding: "8px 16px",
    borderRadius: 8,
    fontSize: 13,
    fontWeight: 500,
    border: `1px solid ${C.accent}`,
    zIndex: 100,
  },
  body: {
    display: "flex",
    flex: 1,
    overflow: "hidden",
  },
  sidebar: {
    width: 320,
    minWidth: 280,
    borderRight: `1px solid ${C.border}`,
    display: "flex",
    flexDirection: "column" as const,
    background: C.surface,
  },
  search: {
    margin: "12px 12px 8px",
    padding: "8px 12px",
    background: C.surface2,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    fontSize: 13,
    outline: "none",
  },
  listScroll: {
    flex: 1,
    overflowY: "auto" as const,
    padding: "0 8px 8px",
  },
  listItem: {
    display: "block",
    width: "100%",
    textAlign: "left" as const,
    padding: "12px 12px",
    margin: "4px 0",
    background: "transparent",
    border: `1px solid transparent`,
    borderRadius: 8,
    cursor: "pointer",
    color: C.text,
    transition: "all 0.15s",
  },
  listItemActive: {
    background: C.accentDim,
    borderColor: C.accent,
  },
  listItemHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
  },
  listItemName: {
    fontSize: 14,
    fontWeight: 600,
  },
  listItemMeta: {
    fontSize: 11,
    color: C.textMuted,
  },
  listItemDesc: {
    fontSize: 12,
    color: C.textMuted,
    margin: "4px 0 6px",
    lineHeight: 1.4,
  },
  tagRow: {
    display: "flex",
    flexWrap: "wrap" as const,
    gap: 6,
  },
  tag: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    background: C.accentDim,
    color: C.accent,
  },
  toolTag: {
    display: "inline-block",
    padding: "2px 8px",
    borderRadius: 4,
    fontSize: 11,
    fontWeight: 500,
    background: "rgba(139,233,253,0.12)",
    color: C.cyan,
  },
  muted: {
    fontSize: 13,
    color: C.textMuted,
    padding: 12,
  },

  /* Detail */
  detail: {
    flex: 1,
    padding: "24px 32px",
    overflowY: "auto" as const,
  },
  detailHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "flex-start",
    marginBottom: 24,
  },
  detailTitle: {
    fontSize: 20,
    fontWeight: 700,
    margin: 0,
    color: C.text,
  },
  detailDesc: {
    fontSize: 14,
    color: C.textMuted,
    margin: "6px 0 0",
    maxWidth: 520,
    lineHeight: 1.5,
  },
  detailMeta: {
    display: "flex",
    alignItems: "center",
    gap: 8,
    marginTop: 8,
    fontSize: 12,
    color: C.textMuted,
  },
  metaDot: {
    width: 3,
    height: 3,
    borderRadius: "50%",
    background: C.textMuted,
  },
  detailActions: {
    display: "flex",
    gap: 8,
    flexShrink: 0,
  },
  block: {
    marginBottom: 20,
    padding: "16px 20px",
    background: C.surface,
    borderRadius: 10,
    border: `1px solid ${C.border}`,
  },
  blockTitle: {
    fontSize: 12,
    fontWeight: 600,
    textTransform: "uppercase" as const,
    letterSpacing: "0.08em",
    color: C.textMuted,
    margin: "0 0 12px",
  },
  pre: {
    margin: 0,
    padding: "10px 14px",
    background: C.bg,
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    fontSize: 13,
    color: C.text,
    whiteSpace: "pre-wrap" as const,
    lineHeight: 1.5,
    fontFamily: "'JetBrains Mono', 'Fira Code', monospace",
  },

  /* Steps */
  stepList: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 10,
  },
  stepCard: {
    padding: "14px 16px",
    background: C.bg,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
  },
  stepHeader: {
    display: "flex",
    alignItems: "center",
    gap: 10,
    marginBottom: 8,
  },
  stepOrder: {
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    width: 24,
    height: 24,
    borderRadius: "50%",
    background: C.accentDim,
    color: C.accent,
    fontSize: 12,
    fontWeight: 700,
    flexShrink: 0,
  },
  stepMode: {
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    padding: "2px 8px",
    borderRadius: 4,
    background: "rgba(189,147,249,0.1)",
    color: C.violet,
    fontSize: 11,
    fontWeight: 500,
  },
  stepInstruction: {
    fontSize: 13,
    color: C.text,
    margin: 0,
    lineHeight: 1.5,
  },
  stepExpected: {
    fontSize: 12,
    color: C.textMuted,
    margin: "6px 0 0",
    fontStyle: "italic" as const,
  },

  /* Empty state */
  emptyDetail: {
    flex: 1,
    display: "flex",
    flexDirection: "column" as const,
    alignItems: "center",
    justifyContent: "center",
    gap: 12,
  },
  emptyIcon: {
    opacity: 0.5,
  },

  /* Form */
  formPane: {
    flex: 1,
    padding: "24px 32px",
    overflowY: "auto" as const,
  },
  formHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 20,
  },
  formGrid: {
    display: "grid",
    gridTemplateColumns: "1fr 1fr",
    gap: 12,
    marginBottom: 12,
  },
  label: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 4,
    fontSize: 12,
    fontWeight: 500,
    color: C.textMuted,
    marginBottom: 8,
  },
  input: {
    padding: "8px 12px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    fontSize: 13,
    outline: "none",
  },
  textarea: {
    padding: "8px 12px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 6,
    color: C.text,
    fontSize: 13,
    outline: "none",
    resize: "vertical" as const,
    width: "100%",
  },
  select: {
    padding: "4px 8px",
    background: C.surface,
    border: `1px solid ${C.border}`,
    borderRadius: 4,
    color: C.text,
    fontSize: 12,
    outline: "none",
  },
  stepsEditorHead: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: 10,
  },
  stepEditor: {
    display: "flex",
    flexDirection: "column" as const,
    gap: 6,
    padding: "12px 14px",
    background: C.surface,
    borderRadius: 8,
    border: `1px solid ${C.border}`,
    marginBottom: 8,
  },
  stepEditorTop: {
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  error: {
    color: C.danger,
    fontSize: 13,
    margin: "8px 0 0",
  },

  /* Buttons */
  btnPrimary: {
    padding: "8px 18px",
    borderRadius: 6,
    border: "none",
    background: C.accent,
    color: C.bg,
    fontSize: 13,
    fontWeight: 600,
    cursor: "pointer",
  },
  btnSecondary: {
    padding: "8px 18px",
    borderRadius: 6,
    border: `1px solid ${C.border}`,
    background: "transparent",
    color: C.text,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnDanger: {
    padding: "8px 18px",
    borderRadius: 6,
    border: `1px solid ${C.danger}`,
    background: C.dangerDim,
    color: C.danger,
    fontSize: 13,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnSmall: {
    padding: "4px 12px",
    borderRadius: 4,
    border: `1px solid ${C.border}`,
    background: C.surface2,
    color: C.accent,
    fontSize: 12,
    fontWeight: 500,
    cursor: "pointer",
  },
  btnRemove: {
    width: 22,
    height: 22,
    borderRadius: 4,
    border: `1px solid ${C.border}`,
    background: C.dangerDim,
    color: C.danger,
    fontSize: 12,
    fontWeight: 700,
    cursor: "pointer",
    display: "inline-flex",
    alignItems: "center",
    justifyContent: "center",
    marginLeft: "auto",
  },
};
